import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Unstable_Grid2';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import { Icon } from '@iconify/react';

import { summarizeWeeklySchedule, getScheduleTypeColor, getScheduleTypeIcon } from '../utils';

const NEW_TIMETABLE_OPTION = '__new__';

function TimetableSelect({ value, timetables, onAssign, onRequestNewTimetable, label = 'Timetable' }) {
  return (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value || ''}
        displayEmpty
        onChange={(e) => {
          const next = e.target.value;
          if (next === NEW_TIMETABLE_OPTION) {
            onRequestNewTimetable();
            return;
          }
          onAssign(next);
        }}
      >
        <MenuItem value="">
          <ListItemIcon>
            <Icon icon="mdi:cancel" width={18} />
          </ListItemIcon>
          <ListItemText primary="No access (unassigned)" />
        </MenuItem>
        <Divider />
        {timetables.map((tt) => (
          <MenuItem key={tt.id} value={tt.id}>
            <ListItemIcon>
              <Box sx={{ display: 'flex', color: `${getScheduleTypeColor(tt.scheduleType)}.main` }}>
                <Icon icon={getScheduleTypeIcon(tt.scheduleType)} width={18} />
              </Box>
            </ListItemIcon>
            <ListItemText primary={tt.name} />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem value={NEW_TIMETABLE_OPTION}>
          <ListItemIcon>
            <Icon icon="mdi:plus" width={18} />
          </ListItemIcon>
          <ListItemText primary="Create custom timetable…" />
        </MenuItem>
      </Select>
    </FormControl>
  );
}

/**
 * Visual grid of gate cards — pick a timetable directly on each gate,
 * no separate "add pairing row" step required.
 */
export default function GateAssignmentGrid({ gates, timetables, assignments, onAssign, onRequestNewTimetable }) {
  const assignedCount = gates.filter((g) => assignments[g.id]).length;

  if (gates.length === 0) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        No gates available yet. Use "Refresh Gates" above once your gate groups are set up.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Chip
          size="small"
          label={`${assignedCount} / ${gates.length} gates assigned`}
          color={assignedCount > 0 ? 'primary' : 'default'}
          variant="outlined"
        />
      </Box>

      <Grid container spacing={2}>
        {gates.map((gate) => {
          const timetableId = assignments[gate.id] || '';
          const timetable = timetables.find((tt) => tt.id === timetableId);
          const scheduleColor = timetable ? getScheduleTypeColor(timetable.scheduleType) : 'default';

          return (
            <Grid key={gate.id} xs={12} sm={6} md={4}>
              <Card
                variant="outlined"
                sx={{
                  p: 2,
                  height: '100%',
                  borderLeft: '4px solid',
                  borderLeftColor: timetable ? `${scheduleColor}.main` : 'divider',
                  transition: 'border-color 0.15s ease',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                  <Icon icon="mdi:gate" width={22} style={{ marginTop: 2, opacity: 0.7 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight="medium" noWrap title={gate.name}>
                      {gate.name || 'Unnamed gate'}
                    </Typography>
                    {gate.groupName && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {gate.groupName}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <TimetableSelect
                  value={timetableId}
                  timetables={timetables}
                  onAssign={(value) => onAssign(gate.id, value)}
                  onRequestNewTimetable={() => onRequestNewTimetable(gate.id)}
                />

                <Box sx={{ mt: 1, minHeight: 24 }}>
                  {timetable ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={scheduleColor}
                      icon={<Icon icon={getScheduleTypeIcon(timetable.scheduleType)} width={14} />}
                      label={summarizeWeeklySchedule(timetable.weeklySchedule)}
                    />
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      This gate won't be included in the access level
                    </Typography>
                  )}
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
